/**
 * Browser entry. Builds the real {@link Platform} from globals, wires theme (localStorage)
 * and the GPS-age ticker — the two things kept out of App so App stays timer- and
 * global-free and integration-testable. A `?demo` harness drives a standalone view with
 * synthetic data so the interface can be seen without hardware.
 */
import { App, openPortDefault, type Platform } from './app';
import { createView, type Mode, type View } from './ui/view';
import { openStore } from './store';
import { watchPosition } from './gps';
import { createTelemetry } from './telemetry';
import type { Upstream } from './outbox';
import { parseRelayBase, type RelayBase } from './relay';
import { createRemote, type OpenStream, type RemoteClient } from './remote';

const THEME_KEY = 'evbweb.theme';
const BUILD = typeof __BUILD__ === 'string' ? __BUILD__ : 'dev';
const FILTERS = [{ vendorId: 0x1a86, productId: 0x7523 }];
const clock = { now: () => new Date(), mono: () => performance.now() };

/**
 * The sink a page with no relay gets. It is a no-op rather than an optional field so that no
 * `if (remote)` branch exists in the read loop or the send path — REQ-REM-1 says the offline page
 * behaves *identically*, and identical is a property of the wiring, not of a condition (02 §3.6).
 */
const NO_RELAY: Upstream = { out: () => {}, note: () => {} };

const RELAY_KEY = 'evbweb.relay.base';

/**
 * `sessionStorage` is not always there to be used. Chrome throws `SecurityError` on the property
 * itself when site data is blocked, and `QuotaExceededError` from `setItem` in a private window
 * with a zero quota.
 *
 * Unguarded, that would be the worst possible failure: `takeRelayBase()` runs before the Platform
 * is built, so a throw rejects `main()` and **the console never starts at all**. A borrowed phone
 * with restrictive settings would open the relay link and get a blank page — the relay costing the
 * entire session, when the governing rule is that it must never be load-bearing (06 §12.12).
 *
 * Degrading instead: the relay works for this page load and does not survive a reload, and
 * de-duplication falls back to in-memory, which is correct for the tab's lifetime anyway.
 */
const session = {
  get(k: string): string | null {
    try {
      return sessionStorage.getItem(k);
    } catch {
      return null;
    }
  },
  set(k: string, v: string): void {
    try {
      sessionStorage.setItem(k, v);
    } catch {
      /* storage blocked or full — see above */
    }
  },
};

const timers = {
  setTimeout: (fn: () => void, ms: number) => window.setTimeout(fn, ms),
  clearTimeout: (h: number) => window.clearTimeout(h),
  now: () => performance.now(),
};

/**
 * Read the relay base out of the fragment, then **remove the fragment whether or not it parsed**
 * (REQ-REM-2). A bad token must not sit in the address bar waiting to be screenshotted, and a good
 * one must not survive into browser history. `search` is kept so `?demo` still works.
 *
 * The page origin stays constant, which is the load-bearing part: a new origin per run would mean
 * a new WebUSB grant and an empty log store every run.
 *
 * @return the base, and — separately — whether a malformed one was seen, because that is worth one
 *         line on screen. An operator who believes they are linked when they are not is worse off
 *         than one who knows they are offline.
 */
function takeRelayBase(): { base: RelayBase | null; malformed: string | null } {
  const parsed = parseRelayBase(location.hash, location.protocol === 'http:' ? 'http:' : 'https:');
  if (location.hash) history.replaceState(null, '', location.pathname + location.search);
  if (parsed.ok) {
    session.set(RELAY_KEY, parsed.value.url);
    return { base: parsed.value, malformed: null };
  }
  // sessionStorage and not localStorage: the phone may be borrowed, so closing the tab IS the
  // detach and needs no UI. It survives a reload in the same tab, which the fragment does not.
  const stored = session.get(RELAY_KEY);
  return {
    base: stored ? { url: stored } : null,
    malformed: parsed.error.kind === 'malformed' ? parsed.error.why : null,
  };
}

/** The real `EventSource`, behind the four-line seam `remote.ts` is written against. */
const openStream: OpenStream = (url, sink) => {
  const es = new EventSource(url); // withCredentials stays false (08 §5.1)
  es.addEventListener('cmd', (e) => sink.onEvent('cmd', e.lastEventId, e.data));
  // Registered unconditionally. An event type the server never sends simply never fires, and this
  // is what lets the page survive a server older than itself — which happens every time the two
  // halves are deployed on different days (02 §5).
  es.addEventListener('ka', (e) => sink.onEvent('ka', e.lastEventId, e.data));
  es.onerror = () => sink.onError();
  return { close: () => es.close() };
};

/**
 * NO HEADERS. `fetch` with a string body sets `Content-Type: text/plain;charset=UTF-8` by itself,
 * which is exactly what keeps this a *simple request* — sent immediately, with no OPTIONS
 * preflight. One added header would put a second round trip in front of EVERY board line, over
 * LTE, on a phone whose battery is the second-ranked field risk (08 §4.2). The token is in the
 * path precisely so that no header is needed.
 */
const post = async (url: string, body: string): Promise<number> =>
  (await fetch(url, { method: 'POST', body })).status;

function loadTheme(): 'light' | 'dark' {
  return localStorage.getItem(THEME_KEY) === 'dark' ? 'dark' : 'light';
}
function nextTheme(t: 'light' | 'dark'): 'light' | 'dark' {
  const next = t === 'dark' ? 'light' : 'dark';
  localStorage.setItem(THEME_KEY, next);
  return next;
}

async function main() {
  const mount = document.getElementById('app');
  if (!mount) return;
  if (new URLSearchParams(location.search).has('demo')) {
    runDemo(mount);
    return;
  }

  // BEFORE anything else runs, and before App is constructed (REQ-REM-2).
  const { base, malformed } = takeRelayBase();
  let remote: RemoteClient | null = null;

  const platform: Platform = {
    store: await openStore(indexedDB),
    build: BUILD,
    ua: navigator.userAgent,
    clock,
    uuid: () => crypto.randomUUID(),
    now: () => new Date(),
    requestDevice: async () => {
      if (!navigator.usb) return null;
      try {
        return await navigator.usb.requestDevice({ filters: FILTERS });
      } catch {
        return null; // cancelled
      }
    },
    openPort: openPortDefault,
    watchGps: (onFix, onError) => {
      if (navigator.geolocation) watchPosition(navigator.geolocation, onFix, onError);
    },
    onDisconnect: (cb) => navigator.usb?.addEventListener('disconnect', cb),
    onVisible: (cb) =>
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') cb();
      }),
    acquireWakeLock: async () => {
      try {
        return (await navigator.wakeLock?.request('screen')) ?? null;
      } catch {
        return null;
      }
    },
    // Resolved lazily because `remote` needs `deliver`, which needs `app`, which needs this
    // object. The indirection costs one closure and keeps the no-op default honest: with no
    // relay this is NO_RELAY for the whole session and nothing else is ever built.
    upstream: {
      out: (line) => (remote?.upstream ?? NO_RELAY).out(line),
      note: (line) => (remote?.upstream ?? NO_RELAY).note(line),
    },
    save: (parts, filename) => {
      const blob = new Blob(parts, { type: 'application/x-ndjson' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.append(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
    },
  };

  let theme = loadTheme();
  const app = new App(
    (h) =>
      createView(mount, {
        ...h,
        onToggleTheme: () => {
          theme = nextTheme(theme);
          app.view.setTheme(theme);
        },
      }),
    platform,
  );
  app.view.setTheme(theme);
  setInterval(() => app.tickGpsAge(), 1000); // display-only age refresh (7.6 s cadence)
  await app.start();

  // A malformed address is worth one line and one err record — the REASON, never the value
  // (REQ-REM-3). It stops nothing else: the page is still the page it was.
  if (malformed) app.view.appendRow('evt', `relay address not usable: ${malformed}`);

  // NO FRAGMENT, NOTHING STORED -> return. No stream, no banner, no timer, no fetch. REQ-REM-1 is
  // an early return rather than a promise: the remote path is code that is not reached.
  if (!base) return;

  remote = createRemote({
    base,
    open: openStream,
    post,
    // The guarded session store above. sessionStorage and not localStorage means a fresh page
    // replays nothing — a page that has just started must not fire an afternoon of accumulated
    // commands into a board (08 §6.3).
    store: session,
    timers,
    deliver: (line) => app.applyRemote(line),
    onState: (s) => app.view.setRemote(s),
  });
  // A hidden tab freezes setInterval and setTimeout (measured, Q7), so on return to the
  // foreground the watchdog checks its own age at once rather than waiting for a tick that did
  // not run. Its own listener rather than App's `onVisible`, which App owns for the wake lock —
  // routing this through App would put a remote concern inside the class that must not have one.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') remote?.poke();
  });
  remote.start();
}

/* ---- demo harness -------------------------------------------------------- */

function runDemo(mount: HTMLElement) {
  let theme = loadTheme();
  let mode: Mode = 'console';
  const tele = createTelemetry();
  const view: View = createView(mount, {
    onConnect: () => {},
    onSend: (t) => feed(`› ${t}`, 'tx'),
    onToggleTheme: () => {
      theme = nextTheme(theme);
      view.setTheme(theme);
    },
    onToggleMode: (m) => {
      mode = m;
      view.setMode(m);
      view.updateGraph(tele.stats(), tele.series());
    },
    onOpenSessions: () =>
      view.openSessions(
        [
          { session: 'a1b2c3d4-past', count: 4021, build: '2026-07-23.1', last: '2026-07-23T11:20:14Z' },
          { session: 'deadbeef-cur', count: 512, build: 'dev', last: '2026-07-23T14:41:02Z' },
        ],
        'deadbeef-cur',
      ),
    onOpenConfig: () =>
      view.openConfig({
        cfg: { freq: 868100000, sf: 10, bw: 125, cr: '4/5', pwr: 0, sync: 0x12 }, // 07 §9.1
        tofdelay: 0,
        pwrmax: 0,
        gpsEnabled: true,
        connected: true,
      }),
    onApplyConfig: (cfg) => feed(`› cfg freq=${cfg.freq} sf=${cfg.sf} bw=${cfg.bw} cr=${cfg.cr} pwr=${cfg.pwr}`, 'tx'),
    onSetTofdelay: (v) => feed(`› cfg tofdelay=${v}`, 'tx'),
    onSetPwrmax: (v) => feed(`› cfg pwrmax=${v}`, 'tx'),
    onUndoConfig: () => feed('› undo (previous config)', 'tx'),
    onToggleGps: () => {},
    onDownload: () => {},
    onDelete: () => {},
  });
  view.setTheme(theme);
  view.setConnection('connected');

  const primer = [
    'ver drv=v1.3.4 fw=0.1.0 chip=1.18',
    'rx seq=118 rxtot=116 rssi=-101.5 snr=-6.75',
    'rx seq=119 rxtot=117 rssi=-102.0 snr=-7.00',
    'rx seq=120 rxtot=118 rssi=-104.5 snr=-7.25',
    'ack seq=120 rxtot=118 frssi=-104.5 fsnr=-7.25 rssi=-98.0 snr=1.50',
  ];
  for (const l of primer) tele.push(l);

  let n = 5;
  let acc = 16;
  let age = 0;
  let seq = 122;

  function feed(text: string, kind: 'rx' | 'tx') {
    view.appendRow(kind, text);
    view.setRecordCount(++n);
    if (kind === 'rx') {
      tele.push(text);
      if (mode === 'graph') view.updateGraph(tele.stats(), tele.series());
    }
  }

  view.setGps(acc, 0);
  setInterval(() => {
    if (seq % 11 === 0) seq++; // an occasional gap for PER
    const rssi = (-100 + Math.sin(seq / 3) * 8).toFixed(1);
    const snr = (-6 + Math.sin(seq / 5) * 3).toFixed(2);
    feed(`rx seq=${seq} rxtot=${seq - 4} rssi=${rssi} snr=${snr}`, 'rx');
    seq++;
  }, 900);
  setInterval(() => view.setGps(acc, (age += 1000)), 1000);
  setInterval(() => {
    acc = Math.max(5, acc - 2);
    age = 0;
  }, 7600);
}

void main();
